import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Crown, TrendingUp, Calendar, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_KEY_ID = 'rzp_test_key'; // Will be fetched from backend

export default function Profile() {
  const { user, usage, fetchUsage } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleUpgrade = async () => {
    setLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/subscription/create`,
        { plan: 'monthly' },
        { withCredentials: true }
      );

      const { subscription_id, key_id } = response.data;

      const options = {
        key: key_id,
        subscription_id: subscription_id,
        name: 'MyTestCase',
        description: 'Monthly Subscription - Unlimited Test Cases',
        handler: async (response) => {
          try {
            await axios.post(
              `${BACKEND_URL}/api/subscription/verify`,
              {
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_subscription_id: response.razorpay_subscription_id,
                razorpay_signature: response.razorpay_signature
              },
              { withCredentials: true }
            );

            toast.success('Subscription activated successfully!');
            await fetchUsage();
            window.location.reload();
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: user?.name,
          email: user?.email
        },
        theme: {
          color: '#3B82F6'
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Subscription creation failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const usagePercentage = usage.tier === 'paid' ? 100 : (usage.used / usage.limit) * 100;

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl font-heading font-bold mb-2">Profile</h1>
            <p className="text-muted-foreground mb-8">Manage your account and subscription</p>

            <div className="grid md:grid-cols-2 gap-6">
              {/* User Info */}
              <Card className="bg-card border-border" data-testid="user-info-card">
                <CardHeader>
                  <CardTitle>Account Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{user?.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{user?.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Tier</p>
                    <div className="flex items-center gap-2">
                      {usage.tier === 'paid' && <Crown className="h-4 w-4 text-yellow-500" />}
                      <span className="font-medium capitalize">{usage.tier}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Usage Stats */}
              <Card className="bg-card border-border" data-testid="usage-stats-card">
                <CardHeader>
                  <CardTitle>Usage Statistics</CardTitle>
                  <CardDescription>Daily generation limit</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-muted-foreground">Today's Usage</span>
                      <span className="text-sm font-medium">
                        {usage.tier === 'paid' ? 'Unlimited' : `${usage.used}/${usage.limit}`}
                      </span>
                    </div>
                    <Progress value={usagePercentage} className="h-2" />
                  </div>

                  {usage.tier !== 'paid' && usage.used >= usage.limit && (
                    <div className="flex items-start gap-2 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                      <AlertCircle className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-yellow-500">Limit Reached</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          You've reached your daily limit. Upgrade for unlimited access.
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Upgrade CTA - Hidden for now */}
            {/* {usage.tier !== 'paid' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mt-8"
              >
                <Card className="bg-gradient-to-br from-blue-600/10 to-violet-600/10 border-blue-500/30" data-testid="upgrade-card">
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Crown className="h-6 w-6 text-yellow-500" />
                      <CardTitle>Upgrade to Unlimited</CardTitle>
                    </div>
                    <CardDescription>Get unlimited test case generations for just $4.50/month</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-center gap-2 text-sm">
                        <TrendingUp className="h-4 w-4 text-green-500" />
                        Unlimited test case generations
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Crown className="h-4 w-4 text-yellow-500" />
                        Ad-free experience
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <Calendar className="h-4 w-4 text-blue-500" />
                        Cancel anytime
                      </li>
                    </ul>
                    <Button
                      onClick={handleUpgrade}
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-blue-600 to-violet-600 hover:opacity-90"
                      data-testid="upgrade-button"
                    >
                      {loading ? 'Processing...' : 'Upgrade Now'}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            )} */}
          </motion.div>
        </div>
      </div>
    </div>
  );
}